import { WindowSize } from "./utils";

export interface PointerPosition {
  x: number;
  y: number;
}

export interface Pointer {
  position: PointerPosition;
  update: (delta: number, smoothing: number) => void;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

// Tracks the pointer in normalized coordinates, where (-1, -1) is the bottom
// left corner and (1, 1) the top right corner of the window. The reported
// position lags behind the pointer, so that it can be used to move the camera
// without the motion feeling abrupt.
export function getPointer(windowSize: WindowSize): Pointer {
  const target = { x: 0, y: 0 };
  const position = { x: 0, y: 0 };

  const setTarget = (clientX: number, clientY: number) => {
    target.x = clamp((2 * clientX) / windowSize.width - 1, -1, 1);
    target.y = clamp(1 - (2 * clientY) / windowSize.height, -1, 1);
  };
  const center = () => {
    target.x = 0;
    target.y = 0;
  };

  window.addEventListener("pointermove", (e: PointerEvent) =>
    setTarget(e.clientX, e.clientY)
  );
  window.addEventListener("pointerdown", (e: PointerEvent) =>
    setTarget(e.clientX, e.clientY)
  );
  // Recenter when the pointer is no longer on the page, and after a touch, so
  // that the scene settles back to its resting position.
  window.addEventListener("pointerup", (e: PointerEvent) => {
    if (e.pointerType != "mouse") center();
  });
  window.addEventListener("pointercancel", center);
  window.addEventListener("blur", center);
  document.addEventListener("pointerleave", center);

  const update = (delta: number, smoothing: number) => {
    const factor = smoothing > 0 ? 1 - Math.exp(-delta / smoothing) : 1;
    position.x += factor * (target.x - position.x);
    position.y += factor * (target.y - position.y);
  };

  return { position, update };
}
